import { defineXray } from "../src/xray/manifest.js";
import { extractLiveOps, describeMissing } from "../extractors/live/index.js";
import { collect, build, groupBy, anchor, ignore } from "../extractors/live/spec.js";
import { SHELL_TOOL_NAMES } from "../src/mine/shell.js";

export default defineXray({
  id: "live",
  label: "Live",
  title: "What an agent did inside running systems — which deployed service it reached into, whether it "
    + "read logs, ran a probe in the container, or changed something, and whether it got an answer",

  consumer: "someone reviewing what an agent did to live infrastructure",
  decision: "whether a session only read from a running service or entered and changed it — and whether what it concluded rests on an answer it actually got",

  // The first lens here whose target passes the identifiability test on evidence
  // rather than intuition: `prod-backend/skwach` is a thing a reader knows without
  // knowing the session, it outlives every session touching it, and more than one
  // session touched it. The pod name would have failed all three.
  tab: true,

  // The call is a shell string, so shell is the only name-level door. Precision
  // comes from the extractor requiring a kubectl-family program with a recognised
  // verb, not from the substrate.
  substrate: { label: "shell (live systems)", match: (n) => SHELL_TOOL_NAMES.test(String(n).split("__").pop()) },

  feed: { domain: "live" },
  extract: extractLiveOps,
  collect,
  group: build,
  groupBy,
  anchor,
  ignore,

  // The service in its environment — the same coordinate groupBy uses, and never
  // the pod, whose suffix is regenerated on every deploy. A namespace-wide survey
  // names no service and so carries no identity; its output still does, which is
  // why the ref is allowed to be null rather than the op being dropped.
  lineage: {
    kind: "live",
    // `<environment>/<namespace>/<service>` — a service in the environment it
    // runs in, which is how anything else in the session would refer to it.
    citable: true,
    ref: (op) => (op.service ? [op.environment, op.namespace, op.service].filter(Boolean).join("/") : null),
    // Only a `change` altered the system; a probe, a log read and an inspect all
    // just looked.
    role: (op) => (op.kind === "change" ? "production" : "consultation"),
    text: (op) => op.output || "",
    label: (op) => `${op.kind} ${[op.environment, op.namespace, op.service].filter(Boolean).join("/") || "an unscoped system"}`,
    intent: (op) => op.found || null,
  },

  emptyNote: "No calls into running systems in this window",
  explainMissing: describeMissing,
});
